"use client";
import { IBlog, IBlogPost, ITheme, IUserData } from "@/types";
import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import moment from "moment";
import { IconArrowBack } from "@tabler/icons-react";
import Link from "next/link";
import { UserURL } from "@/functions";
import { cn, getTextColorForBackground, rgbToHex } from "@/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
    post: IBlogPost;
    user: IUserData;
    impersonate?: boolean;
    theme: ITheme;
    blogTheme?: IBlog["theme"] | null;
    postPage?: boolean;
    children?: React.ReactNode;
    editor?: boolean;
    blog?: IBlog;
    mainSite?: string;
    v2Theme?: IUserData["chosen_theme"]["blog"];
}
export default function BlogPostWrapper({
    post,
    user,
    theme,
    blogTheme,
    postPage,
    children,
    editor,
    blog,
    mainSite,
    v2Theme,
}: Props) {
    const [backgroundImg, setBackgroundImg] = React.useState<string>(post.img);
    const [title, setTitle] = React.useState(post.title);
    const [createdAt, setCreatedAt] = React.useState(post.created_at);
    useEffect(() => {
        const abortController = new AbortController();
        if (editor) {
            window.addEventListener(
                "post-editor-sidebar-updated-background-img",
                (event: any) => {
                    if (event.detail) {
                        setBackgroundImg(event.detail.image);
                    }
                },
                { signal: abortController.signal }
            );
            window.addEventListener(
                "post-editor-sidebar-updated-title",
                (event: any) => {
                    if (event.detail) {
                        setTitle(event.detail.title);
                    }
                },
                { signal: abortController.signal }
            );
            window.addEventListener(
                "post-editor-sidebar-updated-created_at",
                (event: any) => {
                    if (event.detail) {
                        setCreatedAt(event.detail.created_at);
                    }
                },
                { signal: abortController.signal }
            );
        }
        return () => abortController.abort();
    }, [editor]);
    const router = useRouter();
    const color = v2Theme
        ? v2Theme.colors.secondary_text
        : blogTheme?.button_text !== "rgba(0, 0, 0, 0)"
          ? blogTheme?.button_text
          : theme?.text_col_button || "white";
    const headingColor = v2Theme
        ? v2Theme.colors.secondary_text
        : blogTheme?.heading_text !== "rgba(0, 0, 0, 0)"
          ? blogTheme?.heading_text
          : theme?.text_col_heading || "white";
    const backgroundButton = v2Theme
        ? v2Theme.colors.secondary
        : blogTheme?.button_bg !== "rgba(0, 0, 0, 0)"
          ? blogTheme?.button_bg
          : theme.button || "black";
    return (
        <div
            className={`relative flex h-full flex-col gap-2 py-2 ${postPage && "mx-auto w-full max-w-prose"} ${editor && "mx-auto w-full max-w-full"}`}
        >
            <div
                className={`relative flex flex-col gap-2 rounded-xl p-2 ${!editor && "h-full"}`}
                id="Heading"
                style={{
                    background: v2Theme
                        ? v2Theme.colors.secondary
                        : blogTheme?.background || "",
                    color: v2Theme
                        ? v2Theme.colors.secondary_text
                        : getTextColorForBackground(
                              rgbToHex(blogTheme?.background || "")
                          ),
                    fontFamily: "var(--font-heading) !important",
                }}
            >
                <div
                    className={cn(
                        "relative w-full max-w-full overflow-hidden",
                        editor ? "px-12" : "h-full"
                    )}
                    id="blog-header"
                >
                    {backgroundImg ? (
                        <img
                            src={backgroundImg}
                            className={`aspect-video h-auto w-full max-w-full rounded-xl object-cover`}
                            alt={post.title}
                        />
                    ) : (
                        <Skeleton
                            nopulse
                            className={`flex h-52 w-full place-content-center items-center rounded-xl border-4 border-dashed border-muted-foreground/50 bg-accent object-cover`}
                        >
                            <p>No featured image set</p>
                        </Skeleton>
                    )}
                    <div
                        className={`absolute bottom-0 right-0 flex items-center gap-2 overflow-hidden rounded-xl rounded-bl-none rounded-tr-none px-1 py-0.5`}
                        style={{
                            background: v2Theme
                                ? v2Theme.colors.secondary
                                : user.theme.button,
                        }}
                    >
                        {user.is_brand && (
                            <Link
                                href={"https://" + UserURL(user)}
                                className={`flex items-center gap-1 overflow-hidden rounded-xl rounded-bl-none rounded-tr-none transition-all hover:scale-105`}
                                style={{
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : user.theme.button,
                                }}
                            >
                                <img
                                    src={user.pic}
                                    className="h-10 w-10 rounded-full"
                                    alt={user.username}
                                />
                                <p
                                    className="font-bold"
                                    style={{
                                        color: v2Theme
                                            ? v2Theme.colors.primary_text
                                            : user.theme.text_col_button,
                                    }}
                                >
                                    {user.username}
                                </p>
                            </Link>
                        )}
                        <a
                            href={
                                mainSite
                                    ? "https://doras.to/" + mainSite
                                    : "https://" + UserURL(user) + "/blog"
                            }
                        >
                            <Button
                                // Commented out in place for a standard a element
                                // onClick={() => {
                                //     {
                                //         if (mainSite) {
                                //             router.push(
                                //                 "https://doras.to/" + mainSite
                                //             );
                                //         } else {
                                //             router.push(
                                //                 "https://" +
                                //                     UserURL(user) +
                                //                     "/blog"
                                //             );
                                //         }
                                //     }
                                // }}
                                className={`${theme.button_rounded} hidden h-full w-fit gap-1 transition-all hover:scale-105 md:flex`}
                                style={{
                                    color: v2Theme
                                        ? v2Theme.colors.primary_text
                                        : user.theme.text_col_button,
                                    background: v2Theme
                                        ? v2Theme.colors.primary
                                        : user.theme.button,
                                }}
                                aria-label="Go back"
                            >
                                <IconArrowBack />
                            </Button>
                        </a>
                    </div>
                </div>

                <h1
                    className={cn(
                        "py-3 text-4xl font-black",
                        !title && "text-muted-foreground",
                        editor && "px-12"
                    )}
                    style={{
                        color: title && headingColor,
                    }}
                >
                    {post.is_draft && "(Draft) "}
                    {title ? title : "Post Title"}
                </h1>
                {!editor && (
                    <div className="flex flex-col items-start gap-2 md:flex-row">
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger
                                    className="shrink-0"
                                    disabled={!post.updated_at}
                                >
                                    {moment(createdAt).format("Do MMMM, YYYY")}
                                </TooltipTrigger>
                                <TooltipContent
                                    className="text-left"
                                    style={{
                                        background: backgroundButton,
                                        color: color,
                                    }}
                                >
                                    <p
                                        style={{
                                            color: headingColor,
                                        }}
                                    >
                                        Created{" "}
                                        {moment(createdAt).format(
                                            "Do MMMM, YYYY"
                                        )}
                                    </p>
                                    <p
                                        style={{
                                            color: headingColor,
                                        }}
                                    >
                                        Updated{" "}
                                        {moment(post.updated_at).format(
                                            "Do MMMM, YYYY"
                                        )}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}
